import { APP_CONSTANTS } from "@core/constants/app-constants";
import styled from "@emotion/styled";
import { ResponsiveDeviceType, useDimensions } from "@ui/hooks/use-dimensions";
import { NotificationToastContent } from "@ui/pages/notification-center/components/notification-toast-content";
import { physioMeMobileBreakpoint } from "@ui/style";
import { Toast, ToastMessage } from "primereact/toast";
import React, { createContext, PropsWithChildren, useCallback } from "react";

type ToastMessageSeverity = ToastMessage["severity"];

interface ToastContextProps {
	showToast: (summary: string, detail: string, severity?: ToastMessageSeverity) => void;
	showNotification: (message: string, onClick?: () => void) => void;
	clearToasts: () => void;
}

export const ToastContext = createContext<ToastContextProps>({
	showToast: () => undefined,
	showNotification: () => undefined,
	clearToasts: () => undefined,
});

export const ToastProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const toastRef = React.useRef<Toast>(null);

	const showToast = useCallback(
		(summary: string, detail: string, severity: ToastMessageSeverity = "error") =>
			toastRef.current?.show({
				severity,
				summary,
				detail,
				life: APP_CONSTANTS.TOAST_DURATION,
			}),
		[toastRef],
	);

	const showNotification = useCallback(
		(message: string, onClick?: () => void) =>
			toastRef.current?.show({
				severity: "info",
				life: APP_CONSTANTS.TOAST_DURATION,
				content: <NotificationToastContent message={message} onClick={onClick} />,
			}),
		[toastRef],
	);

	const clearToasts = useCallback(() => toastRef.current?.clear(), [toastRef]);

	const { responsiveDeviceType } = useDimensions();

	const contextValue = React.useMemo(() => ({ showToast, showNotification, clearToasts }), [showToast, showNotification, clearToasts]);

	return (
		<ToastContext.Provider value={contextValue}>
			<StyledToast
				ref={toastRef}
				position={responsiveDeviceType === ResponsiveDeviceType.Mobile ? "bottom-center" : "top-right"}
			/>
			{children}
		</ToastContext.Provider>
	);
};

const StyledToast = styled(Toast)`
	.p-toast-icon-close {
		overflow: unset;
		margin-left: 1rem;
	}

	width: auto;
	max-width: 500px;

	@media ${physioMeMobileBreakpoint} {
		bottom: 80px;
		width: 90%;
	}
`;
